import { red } from '@mui/material/colors';
import React, { useContext, useEffect } from 'react';
import { get_cod } from '../../../utils/cod';
import {
  displayLocation,
  extractMapsPlaceLink,
} from '../../../utils/locations';
import { displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import { showDeliveryState } from '../../common/styles/order-states.style';
import {
  getPickupTaskStateColor,
  getPickupTaskStateIcon,
  showPickupTaskChip,
  showPickupTaskState,
} from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { format } from 'date-fns';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { AuthContext } from '../../../contexts/user-context/user-context';
import HouseIcon from '@mui/icons-material/House';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DownloadIcon from '@mui/icons-material/Download';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import CancelIcon from '@mui/icons-material/Cancel';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { DriverWithIcon } from '../../common/styles/driver-with-icon';
import { FactCheckOutlined } from '@mui/icons-material';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { ClearIcon } from '@mui/x-date-pickers';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import NotesIcon from '@mui/icons-material/Notes';
import {
  IMerchantOrdersInvoice,
  MerchantOrdersInvoiceState,
} from '../../../interfaces/common/merchant-invoices';
import {
  canCancelMerchantInvoice,
  showMerchantInvoiceState,
  showPaymentMethod,
} from '../../../utils/merchant-invoices';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import {
  getInvoiceStateColor,
  getInvoiceStateIcon,
} from '../../common/styles/merchant-invoice';
import { API_ENDPOINT } from '../../../configurations/global.config';
import axios from 'axios';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../utils/permissions';

interface ResponsiveMerchantInvoicesListingProps {
  invoicesList: IMerchantOrdersInvoice[];
  isLoading: boolean;
  setLockedInvoiceMeta: React.Dispatch<
    React.SetStateAction<IMerchantOrdersInvoice | null>
  >;
  setLockType: React.Dispatch<React.SetStateAction<'view' | 'delete' | null>>;
  setConfirmUndoInvoice: React.Dispatch<
    React.SetStateAction<IMerchantOrdersInvoice | null>
  >;
  setEditingInvoice: React.Dispatch<
    React.SetStateAction<IMerchantOrdersInvoice | null>
  >;
  handleEditNote: (invoice_pk: string) => Promise<void>;
  fetchInvoiceContent: (invoicemeta: IMerchantOrdersInvoice) => Promise<void>;
  legacyFetchInvoiceContent: (invoicemeta: IMerchantOrdersInvoice) => Promise<void>;
  lockedInvoiceMeta: IMerchantOrdersInvoice | null;
  lockType: 'view' | 'delete' | null;
}

export const ResponsiveMerchantInvoicesListing = ({
  lockType,
  lockedInvoiceMeta,
  setConfirmUndoInvoice,
  fetchInvoiceContent,
  legacyFetchInvoiceContent,
  handleEditNote,
  invoicesList,
  isLoading,
  setEditingInvoice,
  setLockedInvoiceMeta,
  setLockType,
}: ResponsiveMerchantInvoicesListingProps) => {
  const { user, config } = useContext(AuthContext);

  const canEdit = user_has_data_model_access(user, DataResources.MERCHANT_INVOICE, ResourceActionType.UPDATE)
  const canUndo = user_has_data_model_access(user, DataResources.MERCHANT_INVOICE, ResourceActionType.DELETE)
  return (
    <GenericList<IMerchantOrdersInvoice>
      sourceView="merchant-invoices-listing"
      items={invoicesList}
      selectedItems={new Map()}
      updateSelectedItems={() => {
        throw new Error('unimplemented');
      }}
      isLoading={isLoading}
      fullWidth
      columns={[
        {
          id: 'id',
          header: <b>Invoice #</b>,
          display_body: (invoice) => {
            return invoice.status == MerchantOrdersInvoiceState.CANCELLED ? (
              <b>{invoice.id}</b>
            ) : (
              <div
                onClick={() => setEditingInvoice(invoice)}
                style={{ cursor: 'pointer' }}
              >
                <b>
                  <u>{invoice.id}</u>
                </b>
              </div>
            );
          },
        },
        {
          id: 'for',
          header: <b>For</b>,
          display_body: (invoice) => (
            <p>
              <StorefrontIcon color="primary" /> <br /> {invoice.merchant?.name}{' '}
              <br /> {invoice.merchant?.phone_number}
            </p>
          ),
        },
        {
          id: 'package_count',
          header: <b># Packages</b>,
          display_body: (invoice) => <p>{invoice.packages}</p>,
        },
        {
          id: 'due_amount',
          header: <b>Net Due To Merchant</b>,
          display_body: (invoice) => (
            <>{displayMoney(invoice.net_due_to_merchant)}</>
          ),
        },
        {
          id: 'created_at',
          header: <b>Created At</b>,
          display_body: (invoice) => (
            <>{new Date(invoice.created_at).toLocaleString()}</>
          ),
        },
        {
          id: 'payment_state',
          header: <b>Payment State</b>,
          display_body: (invoice) => showMerchantInvoiceState(invoice.status),
        },
        {
          id: 'payment_method',
          header: <b>Payment Method</b>,
          display_body: (invoice) => showPaymentMethod(invoice.payment_method)
        },
        {
          id: 'notes',
          header: <b>Notes</b>,
          display_body: (invoice) => (
            <>
              <IconButton
                onClick={() => {
                  handleEditNote(invoice.id);
                }}
                disabled={!canEdit}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <div style={{ color: 'red' }}>
                {invoice.notes.reverse().join(' | ')}
              </div>
            </>
          ),
        },
        {
          id: 'download_invoice',
          header: <b>Download</b>,
          display_body: (invoice) => (
            <>
              {' '}
              <IconButton
                onClick={() => fetchInvoiceContent(invoice)}
                disabled={
                  lockedInvoiceMeta !== null ||
                  invoice.status == MerchantOrdersInvoiceState.CANCELLED
                }
              >
                {lockedInvoiceMeta?.id === invoice.id && lockType === 'view' ? (
                  <CircularProgress />
                ) : (
                  <DownloadIcon fontSize="large" />
                )}
              </IconButton>
            </>
          ),
        },
        {
          id: 'undo_invoice',
          header: <b>Undo Invoice</b>,
          display_body: (invoice) => (
            <>
              <IconButton
                size="small"
                disabled={
                  lockedInvoiceMeta !== null ||
                  !canCancelMerchantInvoice(invoice) ||
                  !canUndo
                }
                onClick={() => {
                  setLockType('delete');
                  setLockedInvoiceMeta(invoice);
                  setConfirmUndoInvoice(invoice);
                }}
              >
                {lockedInvoiceMeta?.id === invoice.id &&
                lockType === 'delete' ? (
                  <CircularProgress />
                ) : (
                  <RestorePageIcon fontSize="large" />
                )}
              </IconButton>
            </>
          ),
        },
      ]}
      getItemUID={(order) => order.id}
      rowConfigs={[
        {
          display_row: (invoice) => <b>Invoice #{invoice.id}</b>,
        },
        {
          display_row: (invoice) => (
            <MerchantWithIcon centered merchant={invoice.merchant} />
          ),
        },
        {
          display_row: (invoice) => (
            <IconWithText centered>
              <Box
                key={'items'}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '12px',
                  padding: '4px 8px',
                }}
              >
                {invoice.packages} packages
              </Box>
            </IconWithText>
          ),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'amount'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>{'Net Due To Merchant'}</b>{' '}
                {displayMoney(invoice.net_due_to_merchant)}
              </div>,
              <div
                key={'date'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>Date</b>
                {new Date(invoice.created_at).toLocaleString()}
              </div>,
            ]),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'invoice notes'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>Notes</b>
                <>{invoice.notes}</>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditNote(invoice.id);
                  }}
                  disabled={!canEdit}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </div>,
              invoice.status === MerchantOrdersInvoiceState.PENDING_PAYMENT && (
                <div
                  key={'undo invoice'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <b>Undo</b>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setLockType('delete');
                      setLockedInvoiceMeta(invoice);
                      setConfirmUndoInvoice(invoice);
                    }}
                    disabled={lockedInvoiceMeta !== null || !canUndo}
                  >
                    {lockedInvoiceMeta?.id === invoice.id &&
                    lockType === 'delete' ? (
                      <CircularProgress />
                    ) : (
                      <RestorePageIcon
                        fontSize="large"
                        style={{ color: 'red' }}
                      />
                    )}
                  </IconButton>
                </div>
              ),
            ]),
        },
      ]}
      cardFooter={(invoice) => {
        return {
          content: (
            <IconWithText centered>
              {getInvoiceStateIcon(invoice.status)}
              <>{invoice.status.replaceAll('_', ' ')}</>
            </IconWithText>
          ),
          background: getInvoiceStateColor(invoice.status),
        };
      }}
      cardIcon={(invoice) => <MonetizationOnIcon color="primary" />}
      onCardClick={(invoice) => fetchInvoiceContent(invoice)}
    />
  );
};

export default ResponsiveMerchantInvoicesListing;
